// components/foundation/FoundationDetail.tsx

"use client";

import React, { useEffect, useRef, useState } from "react";
import { useParams, useRouter } from "next/navigation";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { getTermsAndPolicies } from "../../../redux/main/mainSlice";
import {
  selectTermsAndPolicies,
  selectMainLoading,
  selectMainError,
} from "../../../redux/selectors/main/mainSelectors";
import { resolveTownLITLink } from "../../../utils/navigation/townlitLinkResolver";
import "./FoundationDetail.css";

const FoundationDetail: React.FC = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const params = useParams();
  const slug = params?.slug as string | undefined;

  const termsAndPolicies = useAppSelector(selectTermsAndPolicies);
  const isLoading = useAppSelector(selectMainLoading).fetchTerms;
  const error = useAppSelector(selectMainError);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [term, setTerm] = useState<typeof termsAndPolicies[0] | undefined>();

  // Load terms if not loaded.
  useEffect(() => {
    if (termsAndPolicies.length === 0) {
      dispatch(getTermsAndPolicies());
    }
  }, [dispatch, termsAndPolicies.length]);

  // Select term by slug.
  useEffect(() => {
    if (slug) {
      const found = termsAndPolicies.find((t) => t.slug === slug);
      setTerm(found);
    }
  }, [slug, termsAndPolicies]);

  // Resolve links inside policy HTML.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a") as HTMLAnchorElement | null;

      if (!anchor) return;

      const href = anchor.getAttribute("href");
      const resolved = resolveTownLITLink(href);

      if (resolved.kind === "foundation" || resolved.kind === "internal") {
        event.preventDefault();
        router.push(resolved.webPath);
        return;
      }

      if (resolved.kind === "external" && resolved.url) {
        anchor.setAttribute("target", "_blank");
        anchor.setAttribute("rel", "noopener noreferrer");
      }
    };

    root.addEventListener("click", handleClick);

    return () => {
      root.removeEventListener("click", handleClick);
    };
  }, [router, term?.id]);

  if (isLoading) {
    return <div className="foundation-message foundation-text-loading">Loading content...</div>;
  }

  if (error) {
    return <div className="foundation-message foundation-text-error">Error loading content.</div>;
  }

  if (!term) {
    return <div className="foundation-message foundation-text-error">Content not found.</div>;
  }

  return (
    <div className="foundation-container">
      <h1 className="foundation-title">{term.title}</h1>

      <div
        ref={contentRef}
        className="foundation-content"
        dangerouslySetInnerHTML={{ __html: term.content }}
      />
    </div>
  );
};

export default FoundationDetail;